import * as Clipboard from 'expo-clipboard';
import { Alert, Linking, Platform } from 'react-native';

/** Cross-platform notice — React Native's `Alert` is a no-op on web. */
function notice(title: string, message: string): void {
  if (Platform.OS === 'web') {
    window.alert(`${title}\n\n${message}`);
  } else {
    Alert.alert(title, message);
  }
}

export type EmailOpenResult = 'opened' | 'copied' | 'unavailable';

/**
 * Open the user's mail client at `address`. `Linking.openURL('mailto:…')`
 * rejects when no handler is registered — common in in-app browsers, the iOS
 * simulator, and on web — which surfaced in Sentry as "Unable to open URL".
 * When opening fails, copy the address to the clipboard so the support path
 * never dead-ends.
 *
 * Returns what happened. By default a built-in cross-platform notice tells the
 * user the address was copied; pass `silent: true` when the caller shows its
 * own feedback (e.g. a screen toast).
 */
export async function openEmail(
  address: string,
  opts: { subject?: string; body?: string; silent?: boolean } = {},
): Promise<EmailOpenResult> {
  const { subject, body, silent } = opts;
  const params = [
    subject ? `subject=${encodeURIComponent(subject)}` : null,
    body ? `body=${encodeURIComponent(body)}` : null,
  ].filter(Boolean);
  const url = params.length ? `mailto:${address}?${params.join('&')}` : `mailto:${address}`;

  try {
    if (await Linking.canOpenURL(url)) {
      await Linking.openURL(url);
      return 'opened';
    }
  } catch {
    // No mail handler — fall through to clipboard.
  }

  let result: EmailOpenResult = 'unavailable';
  try {
    await Clipboard.setStringAsync(address);
    result = 'copied';
  } catch {
    result = 'unavailable';
  }

  if (!silent) {
    notice(
      result === 'copied' ? 'Email copied' : 'Contact support',
      result === 'copied'
        ? `No mail app found. ${address} was copied to your clipboard.`
        : `Email us at ${address}.`,
    );
  }

  return result;
}
